import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  IconButton,
  Stack,
  Alert,
  Grid,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

const EducationTabV2 = ({ education = [], onUpdate, onDirtyChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localEducation, setLocalEducation] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState(null);

  useEffect(() => {
    setLocalEducation([...education]);
  }, [education]);

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(isDirty);
    }
  }, [isDirty, onDirtyChange]);

  const handleEdit = () => setIsEditing(true);

  const handleSave = () => {
    onUpdate(localEducation);
    setIsEditing(false);
    setIsDirty(false);
  };

  const handleCancel = () => {
    setLocalEducation([...education]);
    setIsEditing(false);
    setIsDirty(false);
    setEditingIndex(null);
    setEditForm(null);
  };

  const handleAddNew = () => {
    setEditForm({
      institution: '',
      location: '',
      degrees: []
    });
    setEditingIndex(localEducation.length);
  };

  const handleEditItem = (index) => {
    setEditForm({ ...localEducation[index] });
    setEditingIndex(index);
  };

  const handleSaveItem = () => {
    const updated = [...localEducation];
    if (editingIndex < updated.length) {
      updated[editingIndex] = editForm;
    } else {
      updated.push(editForm);
    }
    setLocalEducation(updated);
    setEditingIndex(null);
    setEditForm(null);
    setIsDirty(true);
  };

  const handleCancelItem = () => {
    setEditingIndex(null);
    setEditForm(null);
  };

  const handleDeleteItem = (index) => {
    if (window.confirm('Delete this education entry?')) {
      const updated = localEducation.filter((_, i) => i !== index);
      setLocalEducation(updated);
      setIsDirty(true);
    }
  };

  const handleAddDegree = () => {
    setEditForm({
      ...editForm,
      degrees: [...editForm.degrees, { degree_type: '', year: '', degree_specialization: '' }]
    });
  };

  const handleUpdateDegree = (degreeIndex, field, value) => {
    const updated = [...editForm.degrees];
    updated[degreeIndex] = { ...updated[degreeIndex], [field]: value };
    setEditForm({ ...editForm, degrees: updated });
  };

  const handleDeleteDegree = (degreeIndex) => {
    const updated = editForm.degrees.filter((_, i) => i !== degreeIndex);
    setEditForm({ ...editForm, degrees: updated });
  };

  const renderItemForm = () => (
    <Card sx={{ mb: 2, bgcolor: 'action.hover' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {editingIndex < localEducation.length ? 'Edit Education' : 'Add Education'}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Institution"
              value={editForm.institution}
              onChange={(e) => setEditForm({ ...editForm, institution: e.target.value })}
              placeholder="e.g., University of Washington"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Location"
              value={editForm.location}
              onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
              placeholder="e.g., Seattle, WA"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" gutterBottom>
          Degrees
        </Typography>

        {editForm.degrees.map((degree, index) => (
          <Box key={index} sx={{ mb: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Degree Type"
                  value={degree.degree_type}
                  onChange={(e) => handleUpdateDegree(index, 'degree_type', e.target.value)}
                  placeholder="e.g., BS, MS, PhD"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Year"
                  value={degree.year}
                  onChange={(e) => handleUpdateDegree(index, 'year', e.target.value)}
                  placeholder="e.g., 2015"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Specialization"
                  value={degree.degree_specialization}
                  onChange={(e) => handleUpdateDegree(index, 'degree_specialization', e.target.value)}
                  placeholder="e.g., Computer Science"
                />
              </Grid>
              <Grid item xs={12} md={1}>
                <IconButton onClick={() => handleDeleteDegree(index)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={handleAddDegree}
          variant="outlined"
          size="small"
        >
          Add Degree
        </Button>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={handleCancelItem} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button onClick={handleSaveItem} variant="contained" startIcon={<SaveIcon />}>
            Save Item
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const renderViewItem = (edu, index) => (
    <Card key={index} sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">{edu.institution}</Typography>
            <Typography variant="body2" color="text.secondary">
              {edu.location}
            </Typography>
            <Box sx={{ mt: 2 }}>
              {edu.degrees.map((degree, degIdx) => (
                <Typography key={degIdx} variant="body1">
                  {degree.degree_type} in {degree.degree_specialization}, {degree.year}
                </Typography>
              ))}
            </Box>
          </Box>
          {isEditing && (
            <Stack direction="row" spacing={1}>
              <IconButton onClick={() => handleEditItem(index)} size="small">
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleDeleteItem(index)} size="small" color="error">
                <DeleteIcon />
              </IconButton>
            </Stack>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Education</Typography>
        {!isEditing ? (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit}>
            Edit
          </Button>
        ) : (
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={!isDirty}>
              Save Changes
            </Button>
          </Stack>
        )}
      </Box>

      {isDirty && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have unsaved changes. Click "Save Changes" to commit them.
        </Alert>
      )}

      {isEditing && editingIndex === null && (
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew} sx={{ mb: 2 }}>
          Add Education
        </Button>
      )}

      {editingIndex !== null && editForm && renderItemForm()}

      {localEducation.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No education added yet.
          {isEditing && ' Click "Add Education" to get started.'}
        </Typography>
      ) : (
        localEducation.map((edu, index) =>
          editingIndex === index ? null : renderViewItem(edu, index)
        )
      )}
    </Box>
  );
};

export default EducationTabV2;
